/* eslint-disable jsx-a11y/tabindex-no-positive */
import { useEventListener, useFocus, useStores } from 'hooks';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { SimLogViewer } from '../../../components/project/ClerkScreen/SimLogViewer/index';

import {
  AskingBid,
  BidIncrementContainer2,
  BlockStatus,
  Chat,
  ConnectDisconnect,
  ConnectionStatusContainer,
  DecisionButtons,
  EndLane,
  FloorBidderBadge,
  ItemDetails,
  ItemNumber,
  LaneStartButtons,
  Logo,
  OpenItem,
  PauseContinueLane,
  SideNavContainer,
  StartingBidAmount,
  Stats,
  TopBarContainer,
  WatchModeOverlay,
  PreviousDecision
} from 'components/project/ClerkScreen';

import {
  AskingBidContainer,
  AuctionStartContainer,
  BidButton,
  BidButtonsContainer,
  BidModifyButtonsContainer,
  DecisionButtonsContainer,
  Layout,
  PageNavContainer,
  HotKeyLabel,
  StyledBidActivity
} from './Styles';

const NPALayout = () => {
  const {
    AuctionStore: {
      auctionID,
      auctionLaneId,
      auctionLaneName,
      auctionList,
      connected,
      getAuctionAndLaneTitle,
      laneClientList,
      laneList,
      laneMetrics,
      laneStatus,
      networkConnected,
      runList,
      runListStats,
    },
    BidStore: {
      auction,
      auctionProduct,
      disableAskingBid,
      lanePaused,
      laneSuspended,
      shouldDisableBidButton,
      shouldDisableFloorBidderOverrideButton,
      shouldDisableOpenItem,
      shouldDisableReRunButton,
      shouldDisableResumeButton,
      shouldDisableRetractBidButton,
      shouldDisableStartingAmountButton,
      shouldDisableStartingBidAmount,
      auctBAFontSize,
    },
    DebuggerStore: { shouldShowLogViewer },
    MessageStore: { auctionLog, directMessageLog, auctionLogObject },
    UIStore: {
      getHotKey,
      handleCurrencyKeyUpFormat,
      handleFloorBidderBidClick,
      handleFloorBidderOverride,
      handleJoinAuction,
      handleJoinLane,
      handleKeyDown,
      handleLogout,
      handleMonitorChatOnly,
      handleOpenItem,
      handleOpenItemChange,
      handleOpenItemClick,
      handleOpenItemFocus,
      handleOpenItemKeyPress,
      handlePauseLane,
      handleReRun,
      handleResume,
      handleRetractBid,
      handleStartEvent,
      handleStartingBidAmountChange,
      handleStartingBidClick,
      handleStartingBidFocus,
      handleStartingBidKeyPress,
    },
  } = useStores();

  const showBidActivity = false;
  const [spaceBarPressedOnBidButton, setSpaceBarPressedOnBidButton] = useState(
    false,
  );
  const [bidButtonEl, setBidButtonElFocus] = useFocus();
  const [startingBidEl, setStartingBidElFocus] = useFocus();
  const [productIdEl, setProductIdElFocus] = useFocus();
  const [floorBidderBadgeEl, setFloorBidderBadgeElFocus] = useFocus();
  const [indvMessageEl, setIndvMessageElFocus] = useFocus();
  const [laneMessageEl, setLaneMessageElFocus] = useFocus();
  const [pinMessageEl, setPinMessageElFocus] = useFocus();

  useEventListener('keydown', handleKeyDown);

  useEffect(() => {
    if (auction.focusOnIndvMessage && indvMessageEl.current) {
      setIndvMessageElFocus();
    }

    if (auction.focusOnLaneMessage && laneMessageEl.current) {
      setLaneMessageElFocus();
    }

    if (auction.focusOnPinMessage && pinMessageEl.current) {
      setPinMessageElFocus();
    }

    const isTypingMessage =
      (auction.focusOnIndvMessage && indvMessageEl.current) ||
      (auction.focusOnLaneMessage && laneMessageEl.current) ||
      (auction.focusOnPinMessage && pinMessageEl.current);

    // console.log('isTypingMessage', isTypingMessage);

    if (!isTypingMessage) {
      if (auction.focusOnProductId && productIdEl.current) {
        setProductIdElFocus();
      }

      if (auction.focusOnFloorBidderBadge && floorBidderBadgeEl.current) {
        setFloorBidderBadgeElFocus();
      }

      if (auction.selectFloorBidderBadge && floorBidderBadgeEl.current) {
        floorBidderBadgeEl.current.select();
      }

      if (auction.focusOnBidButton && bidButtonEl.current) {
        setBidButtonElFocus();
      }
      // TODO: Don't set focus on starting bid amount if an item is not on the block
      if (auction.focusOnStartingBidAmount && startingBidEl.current) {
        setStartingBidElFocus();
      }
    }
    return () => null;
  }, [
    auction.focusOnProductId,
    auction.focusOnFloorBidderBadge,
    auction.selectFloorBidderBadge,
    auction.focusOnStartingBidAmount,
    auction.focusOnBidButton,
    auction.focusOnIndvMessage,
    auction.focusOnLaneMessage,
    auction.focusOnPinMessage,
    auction.lastUIStateChangeTimestamp,
    floorBidderBadgeEl,
    indvMessageEl,
    bidButtonEl,
    laneMessageEl,
    pinMessageEl,
    startingBidEl,
    productIdEl,
    setBidButtonElFocus,
    setFloorBidderBadgeElFocus,
    setIndvMessageElFocus,
    setLaneMessageElFocus,
    setPinMessageElFocus,
    setProductIdElFocus,
    setStartingBidElFocus,
  ]);
  return (
    <>
      <Layout>
        <WatchModeOverlay />
        <TopBarContainer
          connected={connected}
          laneName={auctionLaneName}
          showLogout
          showLaunchPad
          title={getAuctionAndLaneTitle}
        />
        <SideNavContainer
          auctionList={auctionList}
          currentAuctionId={auctionList && auctionID}
          currentLaneId={laneList && auctionLaneId}
          currentLotNumber={auction.lotNumber}
          handleAuctionChange={(e) => handleJoinAuction(e.target.value)}
          handleLaneChange={(e) => handleJoinLane(e.target.value)}
          handleOpenItemClick={handleOpenItemClick}
          laneList={laneList}
          runList={runList}
          totalClerksInLane={laneMetrics && laneMetrics.totalClerks}
          totalClientsInLane={
            laneMetrics &&
            laneMetrics.totalClients - laneMetrics.totalClientsViewOnly
          }
          totalUnitsRemaining={runListStats && runListStats['0']}
          userList={laneClientList}
          zoomLevel={auction.zoomLevel}
        />
        <PageNavContainer>
          <ConnectionStatusContainer
            connected={connected}
            networkConnected={networkConnected}
          />
          <ConnectDisconnect
            connected={connected}
            onCloseConnection={handleLogout}
            onOpenConnection={() =>
              handleStartEvent({
                name: 'Clerk',
                badgeId: 202122,
              })
            }
          />
          <EndLane />
          <PauseContinueLane
            bgColor={!lanePaused ? '#FBCC08' : '#009E0F'}
            bgVariance={20}
            color={!lanePaused ? '#000000' : '#ffffff'}
            icon={lanePaused ? 'fa-play-circle' : 'fa-pause-circle'}
            isLanePaused={lanePaused}
            onClick={handlePauseLane}
            show={laneStatus === 'Running' || laneStatus === 'Paused'}
          />
        </PageNavContainer>
        <Logo
          alt="National Powersport Auctions"
          url={`${process.env.PUBLIC_URL}/assets/logo-inverse.png`}
        >
          <Stats />
        </Logo>
        <ItemNumber />
        <BlockStatus />
        <ItemDetails />
        <AuctionStartContainer>
          <OpenItem
            buttonDisabled={
              !connected ||
              !auction.openItemNumberEntered ||
              auction.openItemNumberEntered === '' ||
              lanePaused ||
              laneSuspended
            }
            buttonIcon="fa-search"
            buttonTabIndex={2}
            inputDisabled={shouldDisableOpenItem}
            inputRef={productIdEl}
            inputTabIndex={1}
            labelText="Open Item #"
            maxLength={5}
            onChange={handleOpenItemChange}
            onClick={handleOpenItem}
            onFocus={handleOpenItemFocus}
            onKeyPress={handleOpenItemKeyPress}
            onKeyUp={() => null}
            placeholderText=""
            testId="open-item-num"
            value={auction.openItemNumberEntered || ''}
          />
          <StartingBidAmount
            buttonDisabled={shouldDisableStartingAmountButton}
            buttonIcon="fa-play-circle"
            buttonTabIndex={4}
            inputDisabled={shouldDisableStartingBidAmount}
            inputRef={startingBidEl}
            inputTabIndex={3}
            labelText="Starting Bid Amount"
            maxLength={11}
            minStartPrice={auctionProduct?.minStartPrice || 0}
            onChange={handleStartingBidAmountChange}
            onClick={() => {
              setBidButtonElFocus();
              handleStartingBidClick(auction.startingBidAmountEntered);
            }}
            onFocus={handleStartingBidFocus}
            onKeyPress={handleStartingBidKeyPress}
            onKeyUp={handleCurrencyKeyUpFormat}
            placeholderText=""
            testId="starting-bid-amt"
            value={auction.startingBidAmountEntered || ''}
          />
          <LaneStartButtons
            disableReRun={shouldDisableReRunButton}
            disableResume={shouldDisableResumeButton}
            handleReRunClick={handleReRun}
            handleResumeClick={handleResume}
          />
        </AuctionStartContainer>
        <BidIncrementContainer2 />
        <AskingBidContainer>
          <AskingBid disabled={disableAskingBid} />
          <BidButtonsContainer>
            <BidButton
              data-testid="bid-button"
              disabled={shouldDisableBidButton}
              onClick={() => {
                if (spaceBarPressedOnBidButton) {
                  setSpaceBarPressedOnBidButton(false);
                  return;
                }
                handleFloorBidderBidClick();
              }}
              onKeyDown={(e) => {
                if (e.which === 32) {
                  setSpaceBarPressedOnBidButton(true);
                }
              }}
              ref={bidButtonEl}
              tabIndex={5}
            >
              Bid
              <HotKeyLabel>
                {getHotKey('target', 'BID') && getHotKey('target', 'BID').label}
              </HotKeyLabel>
            </BidButton>
            <BidModifyButtonsContainer>
              <button
                className="primary retract-bid"
                disabled={shouldDisableRetractBidButton}
                onClick={handleRetractBid}
                type="button"
              >
                Retract Bid
                <HotKeyLabel>
                  {getHotKey('target', 'RETRACT BID') &&
                    getHotKey('target', 'RETRACT BID').label}
                </HotKeyLabel>
              </button>
              <button
                className="primary floor-bid-override"
                disabled={shouldDisableFloorBidderOverrideButton}
                onClick={handleFloorBidderOverride}
                type="button"
              >
                Floor Bidder Override
              </button>
            </BidModifyButtonsContainer>
          </BidButtonsContainer>
        </AskingBidContainer>
        <DecisionButtonsContainer>
          <DecisionButtons source="clerk" />
          <FloorBidderBadge inputRef={floorBidderBadgeEl} />
        </DecisionButtonsContainer>
        <Chat
          auctionLog={auctionLog}
          directMessageLog={directMessageLog}
          handleMonitorChatOnly={handleMonitorChatOnly}
          indvMessageRef={indvMessageEl}
          laneMessageRef={laneMessageEl}
          pinMessageRef={pinMessageEl}
          setIndvMessageElFocus={setIndvMessageElFocus}
          setLaneMessageElFocus={setLaneMessageElFocus}
          setPinMessageElFocus={setPinMessageElFocus}
          showBidActivity={showBidActivity}
          subTitle="National Powersport Auctions"
          title={getAuctionAndLaneTitle}
        />

        {shouldShowLogViewer && <SimLogViewer />}
      </Layout>
    </>
  );
};

export default observer(NPALayout);
